import React from "react";
import get from "lodash/get";
import isNil from "lodash/isNil";
import PublicFormGrid from "../../components/publicForm/PublicFormGrid";
import RffAddressEditingField from "../../components/finalForm/inputs/RffAddressEditingField";
import RffBooleanCheckbox from "../../components/finalForm/inputs/RffBooleanCheckbox";
import RffBooleanToggleField from "../../components/finalForm/inputs/RffBooleanToggleField";
import RffCheckboxArrayField from "../../components/finalForm/inputs/RffCheckboxArrayField";
import RffDateField from "../../components/finalForm/inputs/RffDateField";
import RffDateOfBirthField from "../../components/finalForm/inputs/RffDateOfBirthField";
import RffDollarField from "../../components/finalForm/inputs/RffDollarField";
import RffEmailField from "../../components/finalForm/inputs/RffEmailField";
import RffExitStrategyField from "../../components/finalForm/inputs/RffExitStrategyField";
import RffFileUploadField from "../../components/finalForm/inputs/RffFileUploadField";
import RffNumberField from "../../components/finalForm/inputs/RffNumberField";
import RffPercentField from "../../components/finalForm/inputs/RffPercentField";
import RffPhoneField from "../../components/finalForm/inputs/RffPhoneField";
import RffSelectAutocompleteField from "../../components/finalForm/inputs/RffSelectAutocompleteField";
import RffSelectField from "../../components/finalForm/inputs/RffSelectField";
import RffSelectToggleField from "../../components/finalForm/inputs/RffSelectToggleField";
import RffTextField from "../../components/finalForm/inputs/RffTextField";
import RffTextareaField from "../../components/finalForm/inputs/RffTextareaField";
import RffCheckboxGroup from "../../components/finalForm/shared/RffCheckboxGroup";
import ExperienceSection from "./components/ExperienceSection";
import formatDollar from "../../_src_shared/utils/functions/formatDollar";
import formatDate from "../../_src_shared/utils/functions/formatDate";
import formatAddressObjectFullAddress from "../../_src_shared/utils/functions/formatAddressObjectFullAddress";
import { isExitStrategyHidden } from "../../constants/selectOptions/selectOptionsExitStrategy";
import { selectOptionsEntityType } from "../../constants/selectOptions/selectOptionsEntityType";
import { selectOptionsLoanProductType } from "../../constants/selectOptions/selectOptionsLoanProductType";
import { selectOptionsLoanPurpose } from "../../constants/selectOptions/selectOptionsLoanPurpose";
import { selectOptionsMaritalStatus } from "../../constants/selectOptions/selectOptionsMaritalStatus";
import { selectOptionsPropertyConditionAtOrigination } from "../../constants/selectOptions/selectOptionsPropertyConditionAtOrigination";
import { selectOptionsPropertyType } from "../../constants/selectOptions/selectOptionsPropertyType";
import { selectOptionsUsaStates } from "../../constants/selectOptions/selectOptionsUsaStates";
import {
  CITIZENSHIP_OPTIONS,
  CONSTRUCTION_PRODUCTS,
  DEFAULT_TYPE_OPTIONS,
  EXPERIENCE_CATEGORIES,
  FF_PROPERTY_STATUS_OPTIONS,
  FICO_OPTIONS,
  GUC_PROPERTY_STATUS_OPTIONS,
  HOUSING_EVENT_OPTIONS,
  LOAN_CONTACT_ROLES,
  PRIMARY_RESIDENCE_OWNERSHIP_OPTIONS,
  REFINANCE_PURPOSES,
  TIME_SINCE_HOUSING_EVENT_OPTIONS,
} from "./applicationFormShared";

/**
 * The review step's field model.
 *
 * Every row is a triple: where the answer lives (`name`), how to read it back
 * (`format`), and the SAME Rff* control the original step used (`editor`). The
 * editor being the real component is the whole point — the review edits the one
 * react-final-form store the rest of the form writes to, so a change made here
 * needs no save step, keeps the field's own parsing/validation, and can't drift
 * from the step it mirrors the way a second set of hand-rolled inputs would.
 *
 * `when` mirrors the RffConditional guarding the field on its own step. A row
 * whose condition is false is not "empty" — it doesn't apply, and must not be
 * counted as a missing answer.
 *
 * `format` returning a blank value is the single definition of "unanswered":
 * every empty check in the UI goes through it, so an address object full of
 * empty strings and a missing key behave identically.
 */

const isBlank = (v) =>
  isNil(v) || v === "" || (Array.isArray(v) && v.length === 0);

const yesNo = (v) => (v === true ? "Yes" : v === false ? "No" : null);

const isConstruction = (values) =>
  CONSTRUCTION_PRODUCTS.includes(values?.loanProductType);

const isRefinance = (values) =>
  REFINANCE_PURPOSES.includes(values?.loanPurpose);

const isPurchase = (values) => values?.loanPurpose === "Purchase";

/**
 * Row constructors, one per control type.
 *
 * `path` is passed to the editor at render time rather than closed over, so the
 * same definition serves both a root field and a property field nested under
 * `subjectProperties[i]`.
 */
const T = {
  text: (name, label, o) => ({
    name,
    label,
    ...o,
    format: ({ value }) => (isBlank(value) ? null : String(value)),
    editor: (path) => <RffTextField suppressGrid name={path} />,
  }),

  textarea: (name, label, o) => ({
    name,
    label,
    wide: true,
    ...o,
    format: ({ value }) => (isBlank(value) ? null : String(value)),
    editor: (path) => <RffTextareaField suppressGrid name={path} rows={4} />,
  }),

  email: (name, label, o) => ({
    name,
    label,
    ...o,
    format: ({ value }) => (isBlank(value) ? null : String(value)),
    editor: (path) => <RffEmailField suppressGrid name={path} />,
  }),

  phone: (name, label, o) => ({
    name,
    label,
    ...o,
    format: ({ value }) => (isBlank(value) ? null : String(value)),
    editor: (path) => <RffPhoneField suppressGrid name={path} />,
  }),

  dollar: (name, label, o) => ({
    name,
    label,
    ...o,
    format: ({ value }) => (isBlank(value) ? null : formatDollar(value)),
    editor: (path) => <RffDollarField suppressGrid name={path} />,
  }),

  percent: (name, label, o) => ({
    name,
    label,
    ...o,
    format: ({ value }) => (isBlank(value) ? null : `${value}%`),
    editor: (path) => <RffPercentField suppressGrid name={path} />,
  }),

  number: (name, label, o) => ({
    name,
    label,
    ...o,
    format: ({ value }) =>
      isBlank(value) ? null : Number(value).toLocaleString("en-US"),
    editor: (path) => <RffNumberField suppressGrid name={path} />,
  }),

  date: (name, label, o) => ({
    name,
    label,
    ...o,
    format: ({ value }) => (isBlank(value) ? null : formatDate(value)),
    editor: (path) => <RffDateField suppressGrid name={path} />,
  }),

  bool: (name, label, o) => ({
    name,
    label,
    ...o,
    format: ({ value }) => yesNo(value),
    editor: (path) => <RffBooleanToggleField suppressGrid name={path} />,
  }),

  toggle: (name, label, options, o) => ({
    name,
    label,
    ...o,
    format: ({ value }) => (isBlank(value) ? null : String(value)),
    editor: (path) => (
      <RffSelectToggleField suppressGrid name={path} options={options} />
    ),
  }),

  select: (name, label, options, o) => ({
    name,
    label,
    ...o,
    format: ({ value }) => (isBlank(value) ? null : String(value)),
    editor: (path) => (
      <RffSelectField suppressGrid name={path} options={options} />
    ),
  }),

  autocomplete: (name, label, options, o) => ({
    name,
    label,
    ...o,
    format: ({ value }) => (isBlank(value) ? null : String(value)),
    editor: (path) => (
      <RffSelectAutocompleteField suppressGrid name={path} options={options} />
    ),
  }),

  address: (name, label, o) => ({
    name,
    label,
    wide: true,
    ...o,
    format: ({ value }) => {
      if (isBlank(value)) return null;
      if (typeof value === "string") return value;
      const formatted = formatAddressObjectFullAddress(value);
      return formatted === "N/A" ? null : formatted;
    },
    editor: (path) => <RffAddressEditingField suppressGrid name={path} />,
  }),
};

/**
 * A group of related booleans read back as one "which of these applied" line —
 * the same shape the step asks them in. `names` (not `name`) drives the change
 * tracking, so ticking any one of them marks the row edited.
 */
const flagsRow = ({ label, flags, emptyIsAnswer, ...rest }) => ({
  name: flags[0].name,
  names: flags.map((f) => f.name),
  label,
  wide: true,
  ...rest,
  format: ({ root }) => {
    const on = flags.filter((f) => root?.[f.name] === true);
    if (on.length) return on.map((f) => f.label).join(", ");
    // "Nothing ticked" is a real answer for a disclosure checklist, so these
    // read as answered rather than sitting in the missing-answers count.
    return emptyIsAnswer ? "None reported" : null;
  },
  editor: () => (
    <RffCheckboxGroup>
      {flags.map((f) => (
        <RffBooleanCheckbox key={f.name} name={f.name} label={f.label} />
      ))}
    </RffCheckboxGroup>
  ),
});

/* ---------- Borrower & Loan ---------- */

const borrowerRows = [
  T.text("firstName", "First name", { required: true }),
  T.text("lastName", "Last name", { required: true }),
  T.phone("phone", "Phone", { required: true }),
  T.email("emailAddress", "Email", { required: true }),
  T.toggle("citizenshipStatus", "Citizenship category", CITIZENSHIP_OPTIONS, {
    required: true,
  }),
  T.address("primaryAddress", "Primary address", { required: true }),
  {
    name: "dateOfBirth",
    label: "Date of birth",
    required: true,
    format: ({ value }) => (isBlank(value) ? null : formatDate(value)),
    editor: (path) => <RffDateOfBirthField suppressGrid name={path} />,
  },
  T.toggle("maritalStatus", "Marital status", selectOptionsMaritalStatus, {
    required: true,
  }),
  // The SSN itself is deliberately never echoed back — only whether one exists.
  T.bool("borrowerHasSSN", "Has US Social Security Number", { required: true }),
  T.toggle(
    "loanProductType",
    "Loan product type",
    selectOptionsLoanProductType,
    { required: true, wide: true },
  ),
  T.toggle("loanPurpose", "Loan purpose", selectOptionsLoanPurpose, {
    required: true,
    wide: true,
  }),
  T.date("expectedClosingDate", "Expected closing date"),
  {
    name: "exitStrategy",
    label: "Exit strategy",
    // The field renders nothing for products with no exit strategy; the row has
    // to disappear with it or it would read as an answer they skipped.
    when: (values) => !isExitStrategyHidden(values?.loanProductType),
    format: ({ value }) => (isBlank(value) ? null : String(value)),
    // `null`, not undefined: the field defaults its own label, and a default
    // parameter still fires for undefined — which would print the label twice.
    editor: () => <RffExitStrategyField suppressGrid label={null} />,
  },
  T.bool("brokerDeal", "Working with a third-party broker"),
  {
    name: "uploadMetadata",
    label: "Photo identification",
    required: true,
    wide: true,
    format: ({ root }) => {
      const files = (root?.uploadMetadata || []).filter(
        (f) => f?.docType === "Photo Identification",
      );
      if (!files.length) return null;
      return files.length === 1
        ? files[0].fileName || "1 file attached"
        : `${files.length} files attached`;
    },
    editor: () => (
      <RffFileUploadField
        docGroup="Borrower Documents"
        docType="Photo Identification"
        height="150px"
        suppressGrid
      />
    ),
  },
];

/* ---------- Background & Experience ---------- */

const backgroundRows = [
  T.dollar("cashLiquidity", "Total cash liquidity"),
  T.dollar("otherLiquidity", "Other liquidity sources"),
  T.toggle("estimatedFICO", "Estimated FICO range", FICO_OPTIONS, {
    wide: true,
  }),
  T.toggle(
    "primaryResidenceOwnership",
    "Primary residence",
    PRIMARY_RESIDENCE_OWNERSHIP_OPTIONS,
    { required: true },
  ),
  T.bool("hasEverOwnedPrimaryResidence", "Has ever owned a primary residence", {
    when: (values) => values?.primaryResidenceOwnership === "Rent",
  }),
  flagsRow({
    label: "Credit & legal history",
    emptyIsAnswer: true,
    flags: [
      {
        name: "application_felony_conviction",
        label: "Been convicted of a felony",
      },
      {
        name: "application_financial_fraud_misdemeanor",
        label:
          "Been convicted of any financial or fraud related crimes including misdemeanors",
      },
      {
        name: "bankruptcy_flag",
        label: "Filed for bankruptcy in the last 5 years",
      },
    ],
  }),
  T.bool("open_liens_judgments", "Open charge offs, liens or judgments"),
  flagsRow({
    label: "Which apply",
    when: (values) => values?.open_liens_judgments === true,
    flags: [
      { name: "charge_offs", label: "Charge Offs" },
      { name: "tax_liens", label: "Tax Liens" },
      { name: "financial_judgements", label: "Financial Judgements" },
      { name: "pending_litigation", label: "Pending Litigation" },
    ],
  }),
  {
    name: "housing_events",
    label: "Housing events in the last 3 years",
    wide: true,
    // Stored as the canonical values but shown with this form's labels, so a
    // renamed option can't surface under its old wording here.
    format: ({ value }) => {
      if (isBlank(value)) return "None reported";
      return (Array.isArray(value) ? value : [value])
        .map((v) => HOUSING_EVENT_OPTIONS.find((o) => o.value === v)?.label || v)
        .join(", ");
    },
    editor: (path) => (
      <RffCheckboxArrayField
        suppressGrid
        name={path}
        options={HOUSING_EVENT_OPTIONS}
        columns={2}
      />
    ),
  },
  T.toggle(
    "time_since_housing_event",
    "Time since last housing event",
    TIME_SINCE_HOUSING_EVENT_OPTIONS,
    { when: (values) => !isBlank(values?.housing_events), wide: true },
  ),
  {
    name: EXPERIENCE_CATEGORIES[0].flag,
    names: [
      ...EXPERIENCE_CATEGORIES.map((c) => c.flag),
      ...EXPERIENCE_CATEGORIES.flatMap((c) => c.rows.map((r) => r.name)),
    ],
    label: "Investment experience",
    wide: true,
    format: ({ root }) => {
      const parts = EXPERIENCE_CATEGORIES.filter(
        (c) => root?.[c.flag] === true,
      ).map((category) => {
        const counts = category.rows
          .map((r) => {
            const under = root?.[r.name]?.less_than_three_years;
            const over = root?.[r.name]?.greater_than_three_years;
            if (isBlank(under) && isBlank(over)) return null;
            return `${r.label}: ${under || 0} under 3 yrs · ${over || 0} over 3 yrs`;
          })
          .filter(Boolean);
        return counts.length
          ? `${category.label} — ${counts.join("; ")}`
          : category.label;
      });
      return parts.length ? parts.join("\n") : null;
    },
    editor: () => <ExperienceSection hideSummary />,
  },
  T.textarea("borrowerExperienceSummary", "Experience summary"),
];

/* ---------- Loan Details ---------- */

const loanRows = [
  // On a construction deal this is only the piece funded at closing, and calling
  // it "loan amount" beside a separate draw budget reads as the total.
  T.dollar("baseLoanAmount", "Requested loan amount", {
    labelFor: (values) =>
      isConstruction(values) ? "Initial loan amount" : "Requested loan amount",
  }),
  T.dollar("totalHoldback", "Financed draw budget", { when: isConstruction }),
  T.dollar("totalConstructionBudget", "Total construction budget", {
    when: isConstruction,
  }),
  T.dollar("outstandingLoanUpb", "Outstanding loan payoff", {
    when: isRefinance,
  }),
  // Field-name misspelling is the backend contract — only the label is fixed.
  T.text("exisitingLender", "Current lender", { when: isRefinance }),
  T.date("existingLoanMaturityDate", "Existing loan maturity", {
    when: isRefinance,
  }),
  T.bool("existingLoanLatePayments", "Payments 30+ days late on existing loan", {
    when: isRefinance,
  }),
  T.bool("existingLoanInDefault", "Existing loan in default", {
    when: isRefinance,
  }),
  T.select(
    "existingLoanDefaultType",
    "Default type",
    DEFAULT_TYPE_OPTIONS,
    { when: (values) => values?.existingLoanInDefault === true },
  ),
  T.bool("existingLoanHasUndrawnBudget", "Existing loan has undrawn budget", {
    when: isRefinance,
  }),
  T.dollar("existingLoanUndrawnBudgetAmount", "Undrawn budget amount", {
    when: (values) => values?.existingLoanHasUndrawnBudget === true,
  }),
  T.bool("propertyListedForSale", "Property currently listed for sale", {
    when: isRefinance,
  }),
  T.bool(
    "propertyListedForSaleLastSixMonths",
    "Listed for sale in the last six months",
    { when: isRefinance },
  ),
  // Three fixed slots, pinned to their array index: `loanRole` is stamped on at
  // submit time by position, so a role's card must always write to its own index.
  ...LOAN_CONTACT_ROLES.map((role, index) => {
    const base = `loanRelatedContacts[${index}]`;
    return {
      name: base,
      names: [
        `${base}.firstName`,
        `${base}.lastName`,
        `${base}.emailAddress`,
        `${base}.phone`,
      ],
      label: role,
      wide: true,
      format: ({ root }) => {
        const c = get(root, base) || {};
        const name = [c.firstName, c.lastName].filter(Boolean).join(" ");
        const detail = [name, c.emailAddress, c.phone].filter(Boolean).join(" · ");
        return detail || null;
      },
      editor: () => (
        <PublicFormGrid columns={2}>
          <RffTextField size={1} name={`${base}.firstName`} label="First Name" />
          <RffTextField size={1} name={`${base}.lastName`} label="Last Name" />
          <RffEmailField size={1} name={`${base}.emailAddress`} label="Email" />
          <RffPhoneField size={1} name={`${base}.phone`} label="Phone" />
        </PublicFormGrid>
      ),
    };
  }),
];

/* ---------- Properties ---------- */

/**
 * The per-property question set, in the order the Properties step asks it.
 * Names here are RELATIVE — the review prefixes each with the card's
 * `subjectProperties[i]` path.
 */
export const PROPERTY_ROWS = [
  T.address("address", "Property address", { required: true }),
  T.toggle("propertyType", "Property type", selectOptionsPropertyType, {
    wide: true,
  }),
  T.select(
    "proposedPropertyType",
    "Proposed property type",
    selectOptionsPropertyType,
    { when: (values) => values?.loanProductType === "Ground Up Construction" },
  ),
  T.dollar("asIsValue", "As-is value"),
  T.dollar("arv", "After renovation value", { when: isConstruction }),
  T.dollar("purchasePrice", "Purchase price", { when: isPurchase }),
  T.date("purchaseDate", "Expected purchase date", { when: isPurchase }),
  T.dollar("assignmentFees", "Assignment fees", { when: isPurchase }),
  T.dollar("sellersConcession", "Seller's concession", { when: isPurchase }),
  T.dollar("originalPurchasePrice", "Original purchase price", {
    when: isRefinance,
  }),
  T.date("originalPurchaseDate", "Original purchase date", {
    when: isRefinance,
  }),
  T.bool("propertyListedForSale", "Listed for sale", { when: isRefinance }),
  T.date("lastListedForSaleDate", "Last listed for sale date", {
    when: isRefinance,
  }),
  T.dollar("lastListedForSalePrice", "Last listed for sale price", {
    when: isRefinance,
  }),
  T.dollar("additionalBasis", "Additional basis since purchase", {
    when: (values) => isConstruction(values) && isRefinance(values),
  }),
  T.bool("floodZone", "In a flood zone"),
  T.number("residentialSqFootage", "Residential square footage"),
  T.number("commercialSqFootage", "Commercial square footage", {
    when: (values, property) =>
      ["Mixed-Use", "Commercial", "Other"].includes(property?.propertyType),
  }),
  T.number("afterRenoSqFootage", "Square footage after renovation", {
    when: isConstruction,
  }),
  T.dollar("annualTaxes", "Annual taxes"),
  T.dollar("annualInsurance", "Annual insurance"),
  T.dollar("annualFloodInsurance", "Annual flood insurance"),
  T.dollar("annualHoaFees", "Annual HOA fees"),
  T.dollar("annualPropertyMgmtFees", "Annual property mgmt fees", {
    when: (values) =>
      ["Stabilized Bridge", "DSCR", "Multifamily Bridge"].includes(
        values?.loanProductType,
      ),
  }),
  T.dollar("annualOtherExpense", "Annual other expense"),
  T.toggle(
    "propertyConditionAtOrigination",
    "Property condition at origination",
    selectOptionsPropertyConditionAtOrigination,
    { wide: true },
  ),
  T.select(
    "ffPropertyStatus",
    "Current property status",
    FF_PROPERTY_STATUS_OPTIONS,
    { when: (values) => values?.loanProductType === "Fix and Flip", wide: true },
  ),
  T.select(
    "gucPropertyStatus",
    "Current project status",
    GUC_PROPERTY_STATUS_OPTIONS,
    {
      when: (values) => values?.loanProductType === "Ground Up Construction",
      wide: true,
    },
  ),
  T.dollar("constructionBudget", "Construction budget", {
    when: isConstruction,
  }),
  T.percent("financedBudgetPercent", "Percent of budget financed", {
    when: isConstruction,
  }),
];

/* ---------- Borrowing Entity ---------- */

const hasEntity = (values) => values?.applicantHasExistingEntity === true;
const plansEntity = (values) => values?.applicantHasExistingEntity === false;
const entityAnswered = (values) =>
  hasEntity(values) || plansEntity(values);

const entityRows = [
  T.bool("applicantHasExistingEntity", "Has an existing entity", {
    required: true,
  }),
  T.text("entityName", "Entity name", { when: hasEntity }),
  T.text("entityEINNumber", "EIN", { when: hasEntity }),
  T.percent("applicantEntityPercentOwnership", "Ownership %", {
    when: entityAnswered,
  }),
  T.autocomplete(
    "entityStateOfOrganization",
    "State of organization",
    selectOptionsUsaStates,
    { when: entityAnswered },
  ),
  T.autocomplete("entityType", "Entity type", selectOptionsEntityType, {
    when: entityAnswered,
  }),
  T.date("entityOrganizationDate", "Organization date", { when: hasEntity }),
  T.bool("entityAssets", "Single asset entity", { when: entityAnswered }),
];

/**
 * Sections, in step order. `step` is kept so a row that can't be edited in place
 * still has somewhere to send the applicant.
 */
export const REVIEW_SECTIONS = [
  {
    id: "borrower",
    title: "Borrower & Loan",
    sublabel: "About you and the loan",
    step: 0,
    rows: borrowerRows,
  },
  {
    id: "background",
    title: "Background & Experience",
    sublabel: "Liquidity, credit, track record",
    step: 1,
    rows: backgroundRows,
  },
  {
    id: "loan",
    title: "Loan Details",
    sublabel: "Amount and closing contacts",
    step: 2,
    rows: loanRows,
  },
  {
    id: "properties",
    title: "Properties",
    sublabel: "What's being financed",
    step: 3,
    kind: "properties",
    rows: [],
  },
  {
    id: "entity",
    title: "Borrowing Entity",
    sublabel: "Who holds the loan",
    step: 4,
    rows: entityRows,
  },
];

/**
 * Resolve a row against the current values: does it apply, what does it read
 * back as, and which paths does its change tracking watch.
 *
 * `prefix` scopes a property row to its card; `scope` is the object the row's
 * `when` sees as its local context (the property, or the whole form).
 */
export const resolveRow = (row, { values, prefix = "", scope } = {}) => {
  const localScope = scope || values;
  const path = prefix ? `${prefix}.${row.name}` : row.name;
  const value = get(values, path);
  const label = row.labelFor ? row.labelFor(values) : row.label;

  return {
    row,
    path,
    label,
    required: Boolean(row.required),
    wide: Boolean(row.wide),
    applies: row.when ? Boolean(row.when(values, localScope)) : true,
    display: row.format({ value, root: values, scope: localScope }),
    // Change tracking follows every path the row writes, so a multi-field row
    // (a contact, the experience block) flags as edited from any of them.
    paths: (row.names || [row.name]).map((n) => (prefix ? `${prefix}.${n}` : n)),
  };
};

export { isBlank, isConstruction, isRefinance };
